import { z } from 'zod';
import type { CreateMilestoneCommand, PhaseId } from '@/api/generated';
import { useCreateMilestone } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import styles from './MilestoneDialog.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const schema = z.object({
  title: z.string().trim().min(2, 'Minstens 2 tekens'),
  date: z.string().min(1, 'Kies een datum'),
  phaseId: z.string().min(1, 'Kies een fase'),
});

type MilestoneFormValues = z.infer<typeof schema>;

export function MilestoneCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateMilestone();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuwe mijlpaal</DialogTitle>
        <DialogDescription>
          Markeer een vast moment op de tijdlijn (oplevering, stempel, voorlopige overdracht).
        </DialogDescription>
        <DialogBody>
          <Form<MilestoneFormValues>
            schema={schema}
            defaultValues={{
              title: '',
              date: '',
              phaseId: defaultPhaseId ?? 'Ruw',
            }}
            onSubmit={async (values) => {
              const body: CreateMilestoneCommand = {
                title: values.title.trim(),
                date: values.date,
                phaseId: values.phaseId,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FormField<MilestoneFormValues> name="title" label="Titel" required>
              {(field) => <Input {...field} autoFocus placeholder="Bv. Voorlopige oplevering" />}
            </FormField>
            <FormField<MilestoneFormValues> name="date" label="Datum" required>
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            </FormField>
            <FormField<MilestoneFormValues> name="phaseId" label="Fase" required>
              {(field) => (
                <select
                  {...field}
                  className={styles.select}
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {PHASE_OPTIONS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
